import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { QuickstartCustomAuth } from './QuickstartCustomAuth';
import { QuickstartCard } from './QuickstartCard';
import { QuickstartTheme } from '../types/theme';

interface QuickstartAuthWrapperProps {
  theme: QuickstartTheme;
  authProvider: string;
  onAuthSuccess: (playerAuthId: string, email?: string, persistentId?: string) => void;
  onAuthFailure: (reason: string) => void;
  onClerkUnavailable: () => void;
}

/**
 * Loading spinner shown while Clerk is initializing
 */
const ClerkLoadingSpinner = ({ theme }: { theme: QuickstartTheme }) => (
  <div
    className='flex min-h-screen items-center justify-center overflow-auto p-4'
    style={{
      backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    }}
  >
    <QuickstartCard theme={theme}>
      <div className="flex flex-col items-center text-center">
        <div className='mb-4'>
          <div
            className='mx-auto h-12 w-12 animate-spin rounded-full border-4 border-t-transparent'
            style={{
              borderColor: `${theme.spinnerColor || theme.primaryColor}40`,
              borderTopColor: theme.spinnerColor || theme.primaryColor,
            }}
          />
        </div>
        <p className='text-xl font-semibold text-white'>Loading authentication...</p>
        <p className='mt-2 text-sm text-gray-400'>This should only take a moment...</p>
      </div>
    </QuickstartCard>
  </div>
);

export const QuickstartAuthWrapper = (props: QuickstartAuthWrapperProps) => {
  const { isLoaded } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const hasCalledUnavailableRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // If Clerk loads successfully, show auth immediately
    if (isLoaded) {
      console.log('[QuickstartAuthWrapper] Clerk loaded successfully');
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShowAuth(true);
      return;
    }

    console.log('[QuickstartAuthWrapper] Waiting for Clerk to load...');
    timeoutRef.current = setTimeout(() => {
      if (!isLoaded && !hasCalledUnavailableRef.current) {
        hasCalledUnavailableRef.current = true;
        console.warn('[QuickstartAuthWrapper] Clerk did not load within 10 seconds, redirecting to CTA');
        props.onClerkUnavailable();
      }
    }, 10000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoaded, props]);

  // Show loading spinner while Clerk initializes
  if (!showAuth) {
    return <ClerkLoadingSpinner theme={props.theme} />;
  }

  // Clerk loaded - show auth component
  return (
    <QuickstartCustomAuth
      theme={props.theme}
      authProvider={props.authProvider}
      onAuthSuccess={props.onAuthSuccess}
      onAuthFailure={props.onAuthFailure}
      onClerkUnavailable={props.onClerkUnavailable}
    />
  );
};
